import { useEffect, useMemo, useState } from "react";
import {
  BookOpen, ChevronDown, ChevronRight, Clock, FileText, Link2, PlayCircle,
  ClipboardList, Zap, Plus, X, Presentation, Mic, Video, Image, Upload,
  HelpCircle, Calendar, Monitor, CheckSquare, Send, CheckCircle2, AlertCircle, Pencil,
} from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Badge, Card, PrimaryButton, SectionTitle } from "@/components/Card";
import { useDemoStore } from "@/store/useDemoStore";
import { useEffectiveInstructorId, useGroupsForInstructor, useLmsRoute, useStandards } from "@/store/hooks";
import type { LmsModule } from "@/data/seed";

// ─── Tipos de recursos ───────────────────────────────────────────────────────

type RecursoTipo = "ppt" | "audio" | "video" | "imagen" | "archivo" | "examen" | "preguntas" | "webinar";

type Recurso = {
  id: string;
  tipo: RecursoTipo;
  titulo: string;
  descripcion?: string;
  fecha?: string;
  plataforma?: string;
  duracionMin?: number;
  url?: string;
  estado?: "programado" | "realizado" | "cancelado";
};

// ─── Recursos pre-cargados (demo estático) ───────────────────────────────────

const RECURSOS_INICIALES: Record<string, Recurso[]> = {
  m11: [
    {
      id: "wb-m11-1",
      tipo: "webinar",
      titulo: "Sesión en vivo: Maniobras evasivas con experto en conducción táctica",
      descripcion: "El Mtro. Héctor Vega, campeón nacional de conducción policial, guiará una sesión interactiva con demostración en vivo, análisis de video y sesión de preguntas. Cubre contramanillar, recuperación de trayectoria y protocolo post-esquive.",
      fecha: "2026-06-18",
      plataforma: "Zoom",
      duracionMin: 90,
      url: "https://zoom.us/j/demo",
      estado: "programado",
    },
  ],
};

// ─── Config del picker ────────────────────────────────────────────────────────

const TIPOS: { tipo: RecursoTipo; label: string; icon: React.ReactNode; color: string }[] = [
  { tipo: "ppt",       label: "Presentación",  icon: <Presentation size={22} />, color: "#7c3aed" },
  { tipo: "audio",     label: "Audio",         icon: <Mic size={22} />,          color: "#d97706" },
  { tipo: "video",     label: "Video",         icon: <Video size={22} />,        color: "#dc2626" },
  { tipo: "imagen",    label: "Imagen",        icon: <Image size={22} />,        color: "#059669" },
  { tipo: "archivo",   label: "Archivo",       icon: <Upload size={22} />,       color: "#0369a1" },
  { tipo: "examen",    label: "Examen",        icon: <CheckSquare size={22} />,  color: "#0f766e" },
  { tipo: "preguntas", label: "Preguntas",     icon: <HelpCircle size={22} />,   color: "#9333ea" },
  { tipo: "webinar",   label: "Webinar",       icon: <Monitor size={22} />,      color: "#0284c7" },
];

const kindLabel: Record<string, string> = {
  leccion: "Lección",
  "simulador-preop": "Simulador pre-op",
  "evaluacion-final": "Evaluación final",
};

const kindTone: Record<string, "teal" | "navy" | "amber"> = {
  leccion: "teal",
  "simulador-preop": "amber",
  "evaluacion-final": "navy",
};

const kindIcon: Record<string, React.ReactNode> = {
  leccion: <BookOpen size={14} />,
  "simulador-preop": <Zap size={14} />,
  "evaluacion-final": <ClipboardList size={14} />,
};

// ─── Webinar chip ─────────────────────────────────────────────────────────────

function WebinarChip({ r }: { r: Recurso }) {
  const statusColor = r.estado === "realizado" ? "#059669" : r.estado === "cancelado" ? "#dc2626" : "#0284c7";
  const statusLabel = r.estado === "realizado" ? "Realizado" : r.estado === "cancelado" ? "Cancelado" : "Programado";
  return (
    <div style={{
      border: "1px solid #bae6fd", borderRadius: 10, padding: "12px 14px",
      background: "#f0f9ff", display: "flex", flexDirection: "column", gap: 6,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <Monitor size={15} style={{ color: "#0284c7", flexShrink: 0 }} />
        <span style={{ fontWeight: 700, fontSize: 13, color: "#075985", flex: 1 }}>{r.titulo}</span>
        <span style={{
          fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 20,
          background: `${statusColor}15`, color: statusColor, border: `1px solid ${statusColor}30`,
        }}>{statusLabel}</span>
      </div>
      {r.descripcion && (
        <p style={{ fontSize: 12, color: "#0369a1", margin: 0, lineHeight: 1.5 }}>{r.descripcion}</p>
      )}
      <div style={{ display: "flex", gap: 14, flexWrap: "wrap", fontSize: 11, color: "#0369a1" }}>
        {r.fecha && (
          <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <Calendar size={11} /> {r.fecha}
          </span>
        )}
        {r.plataforma && (
          <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <Monitor size={11} /> {r.plataforma}
          </span>
        )}
        {r.duracionMin && (
          <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <Clock size={11} /> {r.duracionMin} min
          </span>
        )}
        {r.url && (
          <a href={r.url} target="_blank" rel="noreferrer" style={{ color: "#0284c7", textDecoration: "underline" }}>
            Unirse al webinar →
          </a>
        )}
      </div>
    </div>
  );
}

// ─── Chip genérico ────────────────────────────────────────────────────────────

function RecursoChip({ r }: { r: Recurso }) {
  if (r.tipo === "webinar") return <WebinarChip r={r} />;
  const conf = TIPOS.find((t) => t.tipo === r.tipo);
  const color = conf?.color ?? "var(--cc-teal)";
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 8,
      padding: "8px 12px", borderRadius: 8,
      border: `1px solid ${color}30`, background: `${color}08`,
    }}>
      <span style={{ color }}>{conf?.icon}</span>
      <div>
        <div style={{ fontSize: 12, fontWeight: 700, color: "var(--cc-navy)" }}>{r.titulo}</div>
        {r.descripcion && <div style={{ fontSize: 11, color: "var(--cc-gray-500)" }}>{r.descripcion}</div>}
      </div>
    </div>
  );
}

// ─── Modal: Picker de tipo + mini-formulario ──────────────────────────────────

function EnriquecerModal({
  modTitulo,
  onClose,
  onAdd,
}: { modTitulo: string; onClose: () => void; onAdd: (r: Recurso) => void }) {
  const [tipo, setTipo] = useState<RecursoTipo | null>(null);
  const [titulo, setTitulo] = useState("");
  const [fecha, setFecha] = useState("");
  const [plataforma, setPlataforma] = useState("Zoom");
  const [duracion, setDuracion] = useState("60");

  const handleAdd = () => {
    if (!tipo || !titulo.trim()) return;
    const r: Recurso = {
      id: `r-${Date.now()}`,
      tipo,
      titulo: titulo.trim(),
      ...(tipo === "webinar" ? { fecha, plataforma, duracionMin: parseInt(duracion) || 60, estado: "programado" } : {}),
    };
    onAdd(r);
    onClose();
  };

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1000,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
    }} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        background: "white", borderRadius: 14, padding: 28, width: "100%", maxWidth: 520,
        boxShadow: "0 20px 60px rgba(0,0,0,0.18)", display: "flex", flexDirection: "column", gap: 20,
      }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "var(--cc-navy)" }}>Enriquecer módulo</div>
            <div style={{ fontSize: 12, color: "var(--cc-gray-500)", marginTop: 2 }}>{modTitulo}</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--cc-gray-400)", padding: 4 }}>
            <X size={18} />
          </button>
        </div>

        {/* Picker de tipo */}
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--cc-gray-500)", marginBottom: 10 }}>
            Tipo de contenido
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8 }}>
            {TIPOS.map((t) => (
              <button
                key={t.tipo}
                onClick={() => setTipo(t.tipo)}
                style={{
                  display: "flex", flexDirection: "column", alignItems: "center", gap: 6,
                  padding: "12px 8px", borderRadius: 10, border: "2px solid",
                  borderColor: tipo === t.tipo ? t.color : "var(--cc-gray-200)",
                  background: tipo === t.tipo ? `${t.color}10` : "white",
                  cursor: "pointer", transition: "all 0.12s",
                }}
              >
                <span style={{ color: tipo === t.tipo ? t.color : "var(--cc-gray-400)" }}>{t.icon}</span>
                <span style={{ fontSize: 10, fontWeight: 600, color: tipo === t.tipo ? t.color : "var(--cc-gray-500)" }}>
                  {t.label}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Formulario */}
        {tipo && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--cc-gray-500)", marginBottom: 2 }}>
              Datos del recurso
            </div>
            <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: "var(--cc-gray-700)" }}>Título *</span>
              <input
                value={titulo}
                onChange={(e) => setTitulo(e.target.value)}
                placeholder={tipo === "webinar" ? "Ej: Sesión en vivo con experto…" : "Nombre del recurso"}
                style={{
                  padding: "8px 12px", borderRadius: 8, border: "1px solid var(--cc-gray-200)",
                  fontSize: 13, outline: "none", fontFamily: "inherit",
                }}
              />
            </label>

            {tipo === "webinar" && (
              <>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: "var(--cc-gray-700)" }}>Fecha</span>
                    <input
                      type="date"
                      value={fecha}
                      onChange={(e) => setFecha(e.target.value)}
                      style={{
                        padding: "8px 12px", borderRadius: 8, border: "1px solid var(--cc-gray-200)",
                        fontSize: 13, outline: "none", fontFamily: "inherit",
                      }}
                    />
                  </label>
                  <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: "var(--cc-gray-700)" }}>Duración (min)</span>
                    <input
                      type="number"
                      value={duracion}
                      onChange={(e) => setDuracion(e.target.value)}
                      style={{
                        padding: "8px 12px", borderRadius: 8, border: "1px solid var(--cc-gray-200)",
                        fontSize: 13, outline: "none", fontFamily: "inherit",
                      }}
                    />
                  </label>
                </div>
                <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: "var(--cc-gray-700)" }}>Plataforma</span>
                  <select
                    value={plataforma}
                    onChange={(e) => setPlataforma(e.target.value)}
                    style={{
                      padding: "8px 12px", borderRadius: 8, border: "1px solid var(--cc-gray-200)",
                      fontSize: 13, outline: "none", fontFamily: "inherit", background: "white",
                    }}
                  >
                    <option>Zoom</option>
                    <option>Teams</option>
                    <option>Google Meet</option>
                    <option>Otra</option>
                  </select>
                </label>
              </>
            )}
          </div>
        )}

        {/* Acciones */}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, paddingTop: 4, borderTop: "1px solid var(--cc-gray-100)" }}>
          <PrimaryButton variant="ghost" onClick={onClose}>Cancelar</PrimaryButton>
          <PrimaryButton onClick={handleAdd} disabled={!tipo || !titulo.trim()}>
            <Plus size={14} /> Agregar
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
}

// ─── ModuleCard ───────────────────────────────────────────────────────────────

// ─── Modal: Editar módulo ─────────────────────────────────────────────────────

function EditModuleModal({
  titulo, descripcion, duracionMin, onClose, onSave,
}: { titulo: string; descripcion: string; duracionMin: number; onClose: () => void; onSave: (t: string, d: string, dur: number) => void }) {
  const [t, setT] = useState(titulo);
  const [d, setD] = useState(descripcion);
  const [dur, setDur] = useState(String(duracionMin));

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1100,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
    }} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        background: "white", borderRadius: 14, padding: 28, width: "100%", maxWidth: 500,
        boxShadow: "0 20px 60px rgba(0,0,0,0.18)", display: "flex", flexDirection: "column", gap: 18,
      }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "var(--cc-navy)" }}>Editar módulo</div>
            <div style={{ fontSize: 12, color: "var(--cc-gray-500)", marginTop: 2 }}>Modifica los datos generales del módulo</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--cc-gray-400)", padding: 4 }}>
            <X size={18} />
          </button>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: "var(--cc-gray-700)", display: "block", marginBottom: 6 }}>Título</label>
            <input value={t} onChange={(e) => setT(e.target.value)}
              style={{ width: "100%", padding: "8px 12px", borderRadius: 7, border: "1px solid var(--cc-gray-200)", fontSize: 13, boxSizing: "border-box", outline: "none" }} />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: "var(--cc-gray-700)", display: "block", marginBottom: 6 }}>Descripción</label>
            <textarea value={d} onChange={(e) => setD(e.target.value)} rows={3}
              style={{ width: "100%", padding: "8px 12px", borderRadius: 7, border: "1px solid var(--cc-gray-200)", fontSize: 13, resize: "vertical", boxSizing: "border-box", outline: "none", fontFamily: "inherit" }} />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: "var(--cc-gray-700)", display: "block", marginBottom: 6 }}>Duración (minutos)</label>
            <input type="number" value={dur} onChange={(e) => setDur(e.target.value)}
              style={{ width: 120, padding: "8px 12px", borderRadius: 7, border: "1px solid var(--cc-gray-200)", fontSize: 13, outline: "none" }} />
          </div>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <PrimaryButton variant="ghost" onClick={onClose}>Cancelar</PrimaryButton>
          <PrimaryButton onClick={() => onSave(t.trim() || titulo, d, parseInt(dur) || duracionMin)}>
            Guardar cambios
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
}

// ─── Modal: Editar sección ────────────────────────────────────────────────────

function EditSeccionModal({
  label, value, multi, hint, onClose, onSave,
}: { label: string; value: string; multi?: boolean; hint?: string; onClose: () => void; onSave: (v: string) => void }) {
  const [val, setVal] = useState(value);

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1100,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
    }} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        background: "white", borderRadius: 14, padding: 28, width: "100%", maxWidth: 500,
        boxShadow: "0 20px 60px rgba(0,0,0,0.18)", display: "flex", flexDirection: "column", gap: 18,
      }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: "var(--cc-navy)" }}>Editar: {label}</div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--cc-gray-400)", padding: 4 }}>
            <X size={18} />
          </button>
        </div>
        {multi ? (
          <textarea value={val} onChange={(e) => setVal(e.target.value)} rows={6}
            style={{ width: "100%", padding: "10px 12px", borderRadius: 7, border: "1px solid var(--cc-gray-200)", fontSize: 13, resize: "vertical", boxSizing: "border-box", outline: "none", fontFamily: "inherit" }} />
        ) : (
          <input value={val} onChange={(e) => setVal(e.target.value)}
            style={{ width: "100%", padding: "10px 12px", borderRadius: 7, border: "1px solid var(--cc-gray-200)", fontSize: 13, boxSizing: "border-box", outline: "none" }} />
        )}
        {hint && <div style={{ fontSize: 11, color: "var(--cc-gray-500)", marginTop: -10 }}>{hint}</div>}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <PrimaryButton variant="ghost" onClick={onClose}>Cancelar</PrimaryButton>
          <PrimaryButton onClick={() => onSave(val)}>Guardar cambios</PrimaryButton>
        </div>
      </div>
    </div>
  );
}

// ─── Module card ──────────────────────────────────────────────────────────────

const editBtnStyle: React.CSSProperties = {
  background: "none", border: "none", cursor: "pointer", color: "var(--cc-gray-400)",
  padding: "2px 6px", borderRadius: 5, display: "flex", alignItems: "center", flexShrink: 0,
};

function ModuleCard({ mod, onResourceAdded }: { mod: LmsModule; onResourceAdded?: () => void }) {
  const [open, setOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [recursos, setRecursos] = useState<Recurso[]>(() => RECURSOS_INICIALES[mod.id] ?? []);

  // Editable local state
  const [titulo, setTitulo] = useState(mod.titulo);
  const [descripcion, setDescripcion] = useState(mod.descripcion ?? "");
  const [duracionMin, setDuracionMin] = useState(mod.duracionMin);
  const [videoUrl, setVideoUrl] = useState(mod.videoUrl ?? "");
  const [contenido, setContenido] = useState<string[]>(mod.contenido);

  // Edit modals
  const [editModuleOpen, setEditModuleOpen] = useState(false);
  const [editSeccion, setEditSeccion] = useState<{ label: string; value: string; multi?: boolean; hint?: string; onSave: (v: string) => void } | null>(null);
  const [savedFlash, setSavedFlash] = useState(false);

  const showSaved = () => { setSavedFlash(true); setTimeout(() => setSavedFlash(false), 2200); };

  const handleAdd = (r: Recurso) => {
    setRecursos((prev) => [...prev, r]);
    onResourceAdded?.();
  };

  return (
    <>
      {savedFlash && (
        <div style={{
          position: "fixed", bottom: 24, right: 24, zIndex: 2000,
          background: "#052e16", color: "white", borderRadius: 8,
          padding: "10px 16px", display: "flex", alignItems: "center", gap: 8,
          boxShadow: "0 8px 24px rgba(0,0,0,0.2)", fontSize: 13, fontWeight: 600,
        }}>
          <CheckCircle2 size={15} style={{ color: "#86efac" }} />
          Cambios guardados
        </div>
      )}

      {modalOpen && (
        <EnriquecerModal modTitulo={titulo} onClose={() => setModalOpen(false)} onAdd={handleAdd} />
      )}
      {editModuleOpen && (
        <EditModuleModal
          titulo={titulo} descripcion={descripcion} duracionMin={duracionMin}
          onClose={() => setEditModuleOpen(false)}
          onSave={(t, d, dur) => { setTitulo(t); setDescripcion(d); setDuracionMin(dur); setEditModuleOpen(false); showSaved(); onResourceAdded?.(); }}
        />
      )}
      {editSeccion && (
        <EditSeccionModal
          label={editSeccion.label} value={editSeccion.value} multi={editSeccion.multi} hint={editSeccion.hint}
          onClose={() => setEditSeccion(null)}
          onSave={(v) => { editSeccion.onSave(v); setEditSeccion(null); showSaved(); onResourceAdded?.(); }}
        />
      )}

      <div style={{ border: "1px solid var(--cc-gray-200)", borderRadius: 8, overflow: "hidden", background: "white" }}>
        {/* Header row: toggle + edit button as siblings */}
        <div style={{ display: "flex", alignItems: "stretch" }}>
          <button
            onClick={() => setOpen((v) => !v)}
            style={{
              flex: 1, display: "flex", alignItems: "center", gap: 10,
              padding: "12px 12px 12px 16px", background: "none", border: "none", cursor: "pointer", textAlign: "left",
            }}
          >
            <span style={{ color: "var(--cc-gray-400)", flexShrink: 0 }}>
              {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </span>
            <span style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
              <Badge tone={kindTone[mod.kind] ?? "navy"} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                {kindIcon[mod.kind]}
                {kindLabel[mod.kind] ?? mod.kind}
              </Badge>
            </span>
            <span style={{ flex: 1, fontSize: 14, fontWeight: 600, color: "var(--cc-navy)" }}>{titulo}</span>
            <span style={{ display: "flex", gap: 4, flexShrink: 0 }}>
              {recursos.map((r) => {
                const conf = TIPOS.find((t) => t.tipo === r.tipo);
                return (
                  <span key={r.id} title={conf?.label} style={{
                    width: 20, height: 20, borderRadius: 4, background: `${conf?.color ?? "#666"}15`,
                    color: conf?.color ?? "#666", display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    {r.tipo === "webinar" ? <Monitor size={11} /> : r.tipo === "ppt" ? <Presentation size={11} /> :
                     r.tipo === "audio" ? <Mic size={11} /> : r.tipo === "video" ? <Video size={11} /> :
                     r.tipo === "imagen" ? <Image size={11} /> : r.tipo === "examen" ? <CheckSquare size={11} /> :
                     r.tipo === "preguntas" ? <HelpCircle size={11} /> : <Upload size={11} />}
                  </span>
                );
              })}
            </span>
            <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, color: "var(--cc-gray-500)", flexShrink: 0 }}>
              <Clock size={12} /> {duracionMin} min
            </span>
          </button>

          {/* Edit module button */}
          <button
            onClick={() => setEditModuleOpen(true)}
            title="Editar módulo"
            style={{
              padding: "0 14px", background: "none", border: "none",
              borderLeft: "1px solid var(--cc-gray-100)", cursor: "pointer",
              color: "var(--cc-gray-400)", display: "flex", alignItems: "center", flexShrink: 0,
            }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = "var(--cc-teal)"; (e.currentTarget as HTMLButtonElement).style.background = "#f0fdfc"; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = "var(--cc-gray-400)"; (e.currentTarget as HTMLButtonElement).style.background = "none"; }}
          >
            <Pencil size={13} />
          </button>
        </div>

        {open && (
          <div style={{ borderTop: "1px solid var(--cc-gray-100)", padding: "14px 16px", display: "flex", flexDirection: "column", gap: 14 }}>

            {/* Descripción */}
            {descripcion && (
              <div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--cc-gray-500)" }}>Descripción</span>
                  <button style={editBtnStyle} title="Editar descripción"
                    onClick={() => setEditSeccion({ label: "Descripción", value: descripcion, multi: true, onSave: (v) => setDescripcion(v) })}>
                    <Pencil size={12} />
                  </button>
                </div>
                <p style={{ fontSize: 13, color: "var(--cc-gray-600)", margin: 0 }}>{descripcion}</p>
              </div>
            )}

            {/* Video */}
            {videoUrl && (
              <div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--cc-gray-500)" }}>Video</span>
                  <button style={editBtnStyle} title="Editar URL de video"
                    onClick={() => setEditSeccion({ label: "URL del video", value: videoUrl, multi: false, onSave: (v) => setVideoUrl(v) })}>
                    <Pencil size={12} />
                  </button>
                </div>
                <a href={videoUrl} target="_blank" rel="noreferrer"
                  style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--cc-teal)", fontWeight: 500, textDecoration: "none" }}>
                  <PlayCircle size={15} /> Ver video del módulo
                </a>
              </div>
            )}

            {/* Contenidos */}
            {contenido.length > 0 && (
              <div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--cc-gray-500)" }}>Contenidos temáticos</span>
                  <button style={editBtnStyle} title="Editar contenidos"
                    onClick={() => setEditSeccion({ label: "Contenidos temáticos", value: contenido.join("\n"), multi: true, hint: "Un tema por línea", onSave: (v) => setContenido(v.split("\n").map((s) => s.trim()).filter(Boolean)) })}>
                    <Pencil size={12} />
                  </button>
                </div>
                <ul style={{ margin: 0, paddingLeft: 20, display: "flex", flexDirection: "column", gap: 4 }}>
                  {contenido.map((c, i) => (
                    <li key={i} style={{ fontSize: 13, color: "var(--cc-gray-700)" }}>{c}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Meta */}
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
              {mod.quiz.length > 0 && (
                <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--cc-gray-500)" }}>
                  <FileText size={13} />
                  {mod.quiz.length} pregunta{mod.quiz.length !== 1 ? "s" : ""} de evaluación
                </div>
              )}
              {mod.criteriosCubiertos && mod.criteriosCubiertos.length > 0 && (
                <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--cc-gray-500)" }}>
                  <Link2 size={13} />
                  Cubre: {mod.criteriosCubiertos.join(", ")}
                </div>
              )}
            </div>

            {/* Recursos adicionales */}
            {recursos.length > 0 && (
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--cc-gray-500)", marginBottom: 8 }}>
                  Recursos adicionales
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {recursos.map((r) => <RecursoChip key={r.id} r={r} />)}
                </div>
              </div>
            )}

            {/* Botón enriquecer */}
            <div style={{ paddingTop: 4, borderTop: "1px solid var(--cc-gray-100)" }}>
              <PrimaryButton variant="ghost" onClick={() => setModalOpen(true)}>
                <Plus size={13} /> Enriquecer módulo
              </PrimaryButton>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

// ─── Modal de publicación ─────────────────────────────────────────────────────

function PublishModal({
  pendingChanges,
  candidateCount,
  onClose,
  onConfirm,
}: { pendingChanges: number; candidateCount: number; onClose: () => void; onConfirm: () => void }) {
  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1000,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
    }} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        background: "white", borderRadius: 14, padding: 28, width: "100%", maxWidth: 440,
        boxShadow: "0 20px 60px rgba(0,0,0,0.18)", display: "flex", flexDirection: "column", gap: 20,
      }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
          <div style={{ width: 44, height: 44, borderRadius: 10, background: "#eff6ff", color: "#2563eb", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <Send size={20} />
          </div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "var(--cc-navy)", marginBottom: 4 }}>
              Publicar actualización de la ruta formativa
            </div>
            <p style={{ fontSize: 13, color: "var(--cc-gray-600)", margin: 0, lineHeight: 1.5 }}>
              Se empujarán <strong>{pendingChanges} cambio{pendingChanges !== 1 ? "s" : ""}</strong> a los{" "}
              <strong>{candidateCount} candidato{candidateCount !== 1 ? "s" : ""}</strong> inscritos en este estándar.
              Recibirán una notificación con los nuevos recursos disponibles en su ruta de aprendizaje.
            </p>
          </div>
        </div>

        <div style={{ background: "#f8fafc", borderRadius: 8, padding: "12px 14px", border: "1px solid var(--cc-gray-100)" }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--cc-gray-500)", marginBottom: 6 }}>
            ¿Qué ocurre al publicar?
          </div>
          {[
            "Los candidatos ven los nuevos recursos en su ruta de aprendizaje",
            "Los webinars programados aparecen con fecha y enlace de acceso",
            "Se registra la versión publicada con fecha y autor",
          ].map((item, i) => (
            <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: i < 2 ? 6 : 0 }}>
              <CheckCircle2 size={13} style={{ color: "#059669", marginTop: 1, flexShrink: 0 }} />
              <span style={{ fontSize: 12, color: "var(--cc-gray-700)" }}>{item}</span>
            </div>
          ))}
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <PrimaryButton variant="ghost" onClick={onClose}>Cancelar</PrimaryButton>
          <PrimaryButton onClick={onConfirm}>
            <Send size={13} /> Publicar ahora
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
}

// ─── Página principal ─────────────────────────────────────────────────────────

export default function InstructorMaterial() {
  const setRole = useDemoStore((s) => s.setRole);
  useEffect(() => setRole("instructor"), [setRole]);

  const instructorId = useEffectiveInstructorId();
  const groups = useGroupsForInstructor(instructorId);
  const standards = useStandards();

  const codigosUnicos = useMemo(() => {
    const set = new Set<string>();
    for (const g of groups) set.add(g.estandar);
    return [...set];
  }, [groups]);

  const lmsRouteByStandard = useDemoStore((s) => s.lmsRouteByStandard);

  const defaultCodigo = useMemo(
    () => codigosUnicos.find((c) => !!lmsRouteByStandard[c]) ?? codigosUnicos[0],
    [codigosUnicos, lmsRouteByStandard],
  );

  const [selectedCodigo, setSelectedCodigo] = useState<string | undefined>(undefined);
  const activeCodigo = selectedCodigo ?? defaultCodigo;
  const lmsRoute = useLmsRoute(activeCodigo);

  // ── Estado de publicación ─────────────────────────────────────────────────
  const [pendingChanges, setPendingChanges] = useState(0);
  const [publishModalOpen, setPublishModalOpen] = useState(false);
  const [lastPublished, setLastPublished] = useState<string | null>("2026-05-28 09:14");
  const [publishedFlash, setPublishedFlash] = useState(false);

  const candidateCount = useMemo(() => {
    const ids = new Set<string>();
    for (const g of groups) for (const id of (g.candidatosIds ?? [])) ids.add(id);
    return ids.size;
  }, [groups]);

  const handlePublish = () => {
    const now = new Date();
    setLastPublished(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")} ${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`);
    setPendingChanges(0);
    setPublishModalOpen(false);
    setPublishedFlash(true);
    setTimeout(() => setPublishedFlash(false), 3500);
  };

  const totalMin = useMemo(
    () => (lmsRoute?.modules ?? []).reduce((s, m) => s + m.duracionMin, 0),
    [lmsRoute],
  );

  return (
    <AppLayout pageTitle="Ruta formativa" breadcrumb={["Instructor", "Ruta formativa"]}>
      {publishModalOpen && (
        <PublishModal
          pendingChanges={pendingChanges}
          candidateCount={candidateCount}
          onClose={() => setPublishModalOpen(false)}
          onConfirm={handlePublish}
        />
      )}

      {/* Toast de éxito */}
      {publishedFlash && (
        <div style={{
          position: "fixed", top: 20, right: 20, zIndex: 2000,
          background: "#052e16", color: "white", borderRadius: 10,
          padding: "12px 18px", display: "flex", alignItems: "center", gap: 10,
          boxShadow: "0 8px 24px rgba(0,0,0,0.2)", fontSize: 14, fontWeight: 600,
        }}>
          <CheckCircle2 size={18} style={{ color: "#86efac" }} />
          Ruta formativa publicada — candidatos notificados
        </div>
      )}

      {codigosUnicos.length > 1 && (
        <Card style={{ marginBottom: 14 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: "var(--cc-gray-700)" }}>Estándar:</span>
            {codigosUnicos.map((cod) => (
              <button
                key={cod}
                onClick={() => setSelectedCodigo(cod)}
                style={{
                  padding: "4px 12px", borderRadius: 20, border: "1px solid",
                  borderColor: activeCodigo === cod ? "var(--cc-teal)" : "var(--cc-gray-200)",
                  background: activeCodigo === cod ? "var(--cc-teal-light)" : "white",
                  color: activeCodigo === cod ? "var(--cc-teal)" : "var(--cc-gray-600)",
                  fontSize: 12, fontWeight: 600, cursor: "pointer",
                }}
              >
                {cod} — {standards[cod]?.nombre ?? cod}
              </button>
            ))}
          </div>
        </Card>
      )}

      {!lmsRoute ? (
        <Card>
          <div style={{ padding: 32, textAlign: "center", color: "var(--cc-gray-500)", fontSize: 14 }}>
            La ruta de aprendizaje del estándar <strong>{activeCodigo}</strong> aún no ha sido publicada.
          </div>
        </Card>
      ) : (
        <>
          {/* Banner de cambios pendientes */}
          {pendingChanges > 0 && (
            <div style={{
              marginBottom: 14, padding: "10px 16px", borderRadius: 10,
              background: "#fffbeb", border: "1px solid #fde68a",
              display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
            }}>
              <AlertCircle size={15} style={{ color: "#d97706", flexShrink: 0 }} />
              <span style={{ flex: 1, fontSize: 13, color: "#92400e", fontWeight: 500 }}>
                {pendingChanges} cambio{pendingChanges !== 1 ? "s" : ""} sin publicar — los candidatos aún no los ven.
              </span>
              <PrimaryButton onClick={() => setPublishModalOpen(true)}>
                <Send size={13} /> Publicar actualización
              </PrimaryButton>
            </div>
          )}

          <Card style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--cc-gray-500)", marginBottom: 4 }}>
                  Ruta de aprendizaje
                </div>
                <div style={{ fontSize: 20, fontWeight: 700, color: "var(--cc-navy)" }}>
                  {activeCodigo} — {standards[activeCodigo]?.nombre ?? "Estándar"}
                </div>
                {lastPublished && (
                  <div style={{ fontSize: 11, color: "var(--cc-gray-400)", marginTop: 3, display: "flex", alignItems: "center", gap: 4 }}>
                    <CheckCircle2 size={11} style={{ color: "#059669" }} />
                    Última publicación: {lastPublished}
                  </div>
                )}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
                <Stat label="Bloques" value={lmsRoute.blocks.length} />
                <Stat label="Módulos" value={lmsRoute.modules.length} />
                <Stat label="Duración total" value={`${Math.round(totalMin / 60)}h ${totalMin % 60}min`} />
                <div style={{ width: 1, height: 36, background: "var(--cc-gray-100)" }} />
                <PrimaryButton
                  variant={pendingChanges > 0 ? "primary" : "ghost"}
                  onClick={() => setPublishModalOpen(true)}
                >
                  <Send size={13} />
                  {pendingChanges > 0 ? `Publicar (${pendingChanges})` : "Publicar ruta"}
                </PrimaryButton>
              </div>
            </div>
          </Card>

          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            {lmsRoute.blocks.map((block) => {
              const mods = lmsRoute.modules
                .filter((m) => m.blockId === block.id)
                .sort((a, b) => a.orden - b.orden);

              return (
                <div key={block.id}>
                  <SectionTitle>
                    <span style={{ fontSize: 11, color: "var(--cc-gray-400)", marginRight: 8 }}>Bloque {block.orden}</span>
                    {block.titulo}
                  </SectionTitle>
                  {block.descripcion && (
                    <p style={{ fontSize: 13, color: "var(--cc-gray-500)", margin: "4px 0 10px" }}>{block.descripcion}</p>
                  )}
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {mods.map((mod) => (
                      <ModuleCard key={mod.id} mod={mod} onResourceAdded={() => setPendingChanges((n) => n + 1)} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </AppLayout>
  );
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div style={{ textAlign: "center" }}>
      <div style={{ fontSize: 20, fontWeight: 700, color: "var(--cc-navy)" }}>{value}</div>
      <div style={{ fontSize: 11, color: "var(--cc-gray-500)" }}>{label}</div>
    </div>
  );
}
